/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo, useRef } from "react";
import { connect } from "react-redux";
import {
  matchPath,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Menu, Modal } from 'antd';
import { ExclamationCircleOutlined, RightOutlined } from '@ant-design/icons';

import sidenavConfig from "./config";
import { isModuleApproved } from "../../routes/ModuleRouter";
import { isSuperAdmin, tifRoles } from "../../constants/user";
import modules from '../ModuleLoader/modules';
import { actions } from '../../store/reducers/authentication';
import { actions as projectActions } from '../../store/reducers/project';
import { useTranslation } from "react-i18next";
import { CustomIcon } from 'datastake-daf/dist/components';
import { featureIsNotAvailable } from "./helper";
import { formatClassname } from 'datastake-daf/src/helpers/ClassesHelper';
import { getProject } from "../../services/BaseService";
import { NOTIFICATION_MODE, useForms } from "../../context/Forms";
import SidenavMenu from "./components/Menu";
import { getTifModule } from "../../helpers/user";
import { noDatastakeApps } from "../../constants/apps";

const open = false;
const menuMode = 'inline';
const DEFAULT_WIDTH = 200;

function modIcon(code) {
  const { icon: Icon, iconProps } = modules.find(mod => mod.code === code) || {};
  if (Icon) {
    return <Icon style={{ color: iconProps?.style?.color }}></Icon>
  }
}

export const renderModule = ({
  module,
  isCollapsed,
  onClick,
  mod,
}) => {
  if (['tazama'].includes(module)) {
    return <div className="mt-4" />;
  }

  return (
    <div className={formatClassname(['mod-name', isCollapsed ? 'is-collapsed' : 'not-collapsed', mod === 'PARTNER' && 'mt-8'])}>
      {(mod || '').toUpperCase() !== 'ADMIN' ? mod === 'PARTNER' ? (
        <div className="icon cursor-pointer" onClick={onClick}>
          <div className="icon">
            <img src="/assets/images/SVG/partner.svg" alt="partner" />
          </div>
        </div>
      ) : (
        <div className="icon cursor-pointer" onClick={onClick}>
          <div className="icon">
            {modIcon((mod || '').toUpperCase())}
          </div>
        </div>
      ) : (
        <div className="icon">
          <CustomIcon name="AdminIcon" width={14} height={14} />
        </div>
      )}

      {isCollapsed ? null : <h1>{(module || '').toUpperCase()}</h1>}
    </div>
  )
}

const Sidenav = ({
  authentication: { user, selectedProject },
  project: { data: { projects } },
  module,
  changeSelectedProjectAndSourceId,
  isCollapsed = false,
  showMenu = true,
}) => {
  const hoverDiv = useRef();
  const hoverDivSecond = useRef();
  const { t } = useTranslation();
  const items = useMemo(() => sidenavConfig[module] || [], [module]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [project, setProject] = useState({});
  const [topHover, setTopHover] = useState(-100);
  const [topHoverSecond, setTopHoverSecond] = useState(-100);
  const [widthHoverSecond, setWidthHoverSecond] = useState(DEFAULT_WIDTH);
  const [hoverOpen, setHoverOpen] = useState(false);
  const [hoverItem, setHoverItem] = useState(null);
  const [hoverItemSecond, setHoverItemSecond] = useState(null);
  const isDemo = process.env.REACT_APP_ENV === 'demo' && !isSuperAdmin(user);
  const isDev = ['dev', 'development', 'local', 'demo'].includes(process.env.REACT_APP_ENV);
  const goTo = useNavigate();
  const { pathname } = useLocation();
  const { notificationMode, changeNotificationState } = useForms();

  const mod = useMemo(() => {
    if (module === 'tif') {
      if (user?.appRole?.code === tifRoles.PARTNER) {
        return 'PARTNER';
      }

      return getTifModule(user);
    }

    return module;
  }, [module, user]);

  const checkPath = (i) => {
    return (matchPath({
      path: i.path,
      exact: true,
    }, pathname) || (i.subPath && i.subPath.filter(s => matchPath({
      path: s,
      exact: true,
    }, pathname)).length)) ? true : false;
  }

  useEffect(() => {
    const proj = (projects || []).find(p => p.id === (selectedProject
      ? selectedProject : getProject()));
    setProject(proj || {});
  }, [selectedProject, module, projects])

  useEffect(() => {
    if (module !== 'app' && !isModuleApproved(user, module) && !isSuperAdmin(user)) {
      redirectToQuestionnaire();
    }
  }, []);

  useEffect(() => {
    setHoverItem(null);
    setHoverOpen(false);
    setHoverItemSecond(null);
    setTopHover(-100);
    setTopHoverSecond(-100);
    setWidthHoverSecond(DEFAULT_WIDTH);
  }, [isCollapsed])

  function redirectToQuestionnaire() {
    goTo(`/app/${module}/profile?create`);
  }

  function notApprovedModal(path) {
    if (![`/app/${module}/profile?create`, `/app/${module}/profile`].includes(path)) {
      Modal.confirm({
        title: 'Pending module verification',
        icon: <ExclamationCircleOutlined />,
        content: 'You can’t access this module because you are not yet approved by Datastake Admin',
        okText: 'Ok',
        cancelButtonProps: {
          style: {
            display: 'none',
          },
        },
        onOk: () => redirectToQuestionnaire(),
      })
    }
  }

  const onMouseMove = (event) => {
    let insideMain = false;

    if (hoverDiv.current) {
      const divRect = hoverDiv.current.getBoundingClientRect();
      const isInside = event.clientX >= divRect.left && event.clientX <= divRect.right &&
        event.clientY >= divRect.top && event.clientY <= divRect.bottom
      insideMain = isInside;
    }

    if (!insideMain && hoverDivSecond.current) {
      const divRect = hoverDivSecond.current.getBoundingClientRect();
      const isInside = event.clientX >= divRect.left && event.clientX <= divRect.right &&
        event.clientY >= divRect.top && event.clientY <= divRect.bottom
      if (!isInside) {
        setHoverItemSecond(null);
        document.removeEventListener('mousemove', onMouseMove);
        setHoverItem(null);
        setHoverOpen(false);
        setTopHover(-100);
        setTopHoverSecond(-100);
        setWidthHoverSecond(DEFAULT_WIDTH);
      }
    }
  }

  const checkOnClick = ({ event }) => {
    if (notificationMode === NOTIFICATION_MODE.PROJECT_SOURCES) {
      changeNotificationState({ onYes: event });
    } else {
      event();
    }
  }

  const hoverContent = useMemo(() => (
    hoverItem && hoverItem.items ? <>
      <header
        onClick={() => {
          checkOnClick({
            event: () => {
              // if (hoverItem.onTitleClick === 'PUSH_TO_DD_ANALYSIS') {
              //   goTo(`/app/${module}/analysis/dd`);
              // }
            }
          })
        }}
      >
        <CustomIcon width={15} height={15} name={hoverItem.icon} />
        <span>{t(hoverItem.name)}</span>
      </header>
      <Menu
        items={hoverItem.items.filter(item => item.visible ? item.visible(user, project) : false).map((subitem, j) => ({
          key: subitem.path,
          className: formatClassname([
            subitem.path === pathname ? 'ant-menu-item-selected' : '',
            subitem.items ? 'with-subitems' : '',
          ]),
          style: subitem.style ? subitem.style : {},
          disabled: hoverItem.isDisabled || subitem.isDisabled,
          onClick: (e) => {
            if (subitem.items) {
              setHoverItemSecond(subitem);
              setTopHoverSecond(e.domEvent.currentTarget.getBoundingClientRect().top)
              setWidthHoverSecond(e.domEvent.currentTarget.getBoundingClientRect().width + 34);
              return;
            }

            checkOnClick({
              event: () => {
                document.removeEventListener('mousemove', onMouseMove);
                setHoverItem(null);
                setHoverItemSecond(null);
                setHoverOpen(false);
                setTopHover(-100);
                setTopHoverSecond(-100);
                setWidthHoverSecond(DEFAULT_WIDTH);
                if (isModuleApproved(user, module) || isSuperAdmin(user)) {
                  if ((isDemo && !subitem.demo) || (subitem.isDisabled && subitem.isDisabled(user) && !isDev)) {
                    featureIsNotAvailable();
                    return;
                  }
                  if (subitem.path) {
                    goTo(subitem.path);
                  }
                } else {
                  notApprovedModal(subitem.path)
                }
              }
            })
          },
          label: (
            <div className="flex flex-row flex-1">
              <div className="flex-1">
                {t(subitem.name)}
              </div>
              <div className="flex flex-column justify-content-center sub-icon">
                {!!subitem.items && (
                  <RightOutlined style={{ fontSize: 12 }} />
                )}
              </div>
            </div>
          ),
        }))}
      />
    </> : hoverItem ? <>
      <Menu
        items={[
          {
            className: hoverItem.path === pathname ? 'ant-menu-item-selected' : '',
            key: hoverItem.path,
            style: hoverItem.style ? hoverItem.style : {},
            icon: <CustomIcon width={15} height={15} name={hoverItem.icon} />,
            disabled: hoverItem.isDisabled,
            onClick: () => {
              checkOnClick({
                event: () => {
                  if (isModuleApproved(user, module) || isSuperAdmin(user)) {
                    if ((isDemo && !hoverItem.demo) || (hoverItem.isDisabled && hoverItem.isDisabled(user) && !isDev)) {
                      featureIsNotAvailable();
                      return;
                    }
                    if (hoverItem.path) {
                      goTo(hoverItem.path);
                    }
                  } else {
                    notApprovedModal(hoverItem.path)
                  }
                }
              })
            },
            label: t(hoverItem.name),
          }
        ]} />
    </> : null
  ), [hoverItem]);

  const hoverContentSecond = useMemo(() => hoverItemSecond ? (
    <Menu
      items={hoverItemSecond.items.filter(item => item.visible ? item.visible(user, project) : false).map((subitem, j) => ({
        key: subitem.path,
        className: subitem.path === pathname ? 'ant-menu-item-selected' : '',
        style: subitem.style ? subitem.style : {},
        disabled: hoverItemSecond.isDisabled || subitem.isDisabled,
        onClick: () => {
          checkOnClick({
            event: () => {
              document.removeEventListener('mousemove', onMouseMove);
              setHoverItem(null);
              setHoverOpen(false);
              setTopHover(-100);
              setTopHoverSecond(-100);
              setWidthHoverSecond(DEFAULT_WIDTH);
              setHoverItemSecond(null);
              if (isModuleApproved(user, module) || isSuperAdmin(user)) {
                if ((isDemo && !subitem.demo) || (subitem.isDisabled && subitem.isDisabled(user) && !isDev)) {
                  featureIsNotAvailable();
                  return;
                }
                if (subitem.path) {
                  goTo(subitem.path);
                }
              } else {
                notApprovedModal(subitem.path)
              }
            }
          })
        },
        label: t(subitem.name),
      }))}
    />
  ) : null, [hoverItemSecond])

  const filteredItems = useMemo(() => {
    const mapItems = (item) => {
      const isDisabled = typeof item.isDisabled === 'function' ?
        item.isDisabled(user, project, selectedProject)
        : item.isDisabled || false;

      if (item.items) {
        return {
          ...item, isDisabled, items: item.items.filter((item) => {
            const res = typeof item.visible === 'function' ? item.visible(user, project, selectedProject) : true;
            return res;
          }).map(mapItems)
        };
      }

      return { ...item, isDisabled };
    };

    return items
      .filter(item => {
        const res = item.visible(user, project, selectedProject);
        return res;
      })
      .map(mapItems);
  }, [items, user, project, selectedProject, pathname]);

  useEffect(() => {
    const selectedItem = filteredItems
      .filter((d) => !d.isDisabled)
      .find(i => i.items ? i.items.filter(ii => !ii.isDisabled && checkPath(ii)).length > 0 : checkPath(i));

    if (selectedItem) {
      if (selectedItem.items) {
        const subItem = selectedItem.items.find(ii => checkPath(ii));
        if (subItem) {
          setSelectedKeys([subItem.path]);
        }
      } else {
        setSelectedKeys([selectedItem.path]);
      }
    } else {
      setSelectedKeys([pathname]);
    }
  }, [pathname])

  const userModule = useMemo(() => {
    if (user?.appRole?.code === tifRoles.AMP) {
      return 'AMP';
    }

    if (user?.appRole?.code === tifRoles.PARTNER) {
      return 'PARTNER';
    }

    return module;
  }, [user, module]);

  return <React.Fragment>
    {renderModule({
      module: userModule,
      isCollapsed,
      onClick: () => checkOnClick({
        event: () => {
          goTo(`/app/${module}`);
          changeSelectedProjectAndSourceId('myOwnData', user?.company?.id)
          localStorage.setItem('project', 'myOwnData');
        }
      }),
      mod,
    })}
    <div className={formatClassname(['sidenav-cont', module])}>
      <div className={formatClassname(['menus-cont', isCollapsed ? 'is-collapsed' : 'not-collapsed'])}>
        {showMenu ? (
          <SidenavMenu
            module={module}
            menuMode={menuMode}
            open={open}
            selectedKeys={selectedKeys}
            filteredItems={filteredItems}
            isCollapsed={isCollapsed}
            setHoverItem={setHoverItem}
            setTopHover={setTopHover}
            setHoverOpen={setHoverOpen}
            onMouseMove={onMouseMove}
            user={user}
            goTo={goTo}
            notApprovedModal={notApprovedModal}
            isDemo={isDemo}
            isDev={isDev}
            t={t}
            checkOnClick={checkOnClick}
            project={project}
          />
        ) : null}

      </div>
      <div
        ref={hoverDiv}
        className={formatClassname(["sidenav-hover-content", module])}
        style={{
          top: topHover,
          display: hoverOpen ? 'flex' : 'none'
        }}>
        {hoverContent}
      </div>
      <div
        ref={hoverDivSecond}
        className={formatClassname(["sidenav-hover-content", module])}
        style={{
          top: topHoverSecond,
          display: hoverItemSecond ? 'flex' : 'none',
          left: 70 + widthHoverSecond,
        }}>
        {hoverContentSecond}
      </div>
      {!isCollapsed ? !noDatastakeApps.includes(mod) ? (
        <div className="sidenav-footer">
          <h6>{t('Powered By')}</h6>
          <img src="/assets/images/datastake-white.svg" alt="logo" />
        </div>
      ) : null : null}
    </div>
  </React.Fragment>
};

export default connect(({ cadd, authentication, project }) => ({
  cadd,
  authentication,
  project,
}), { ...actions, ...projectActions })(Sidenav);
